import type { ThemeConfig } from "./skins";

/**
 * Contrast validation for a brand, with the FIX rather than a complaint.
 *
 * The existing validator checked three pairings and warned. It missed the case
 * that actually fails, and this session found it the slow way — by measuring ~120
 * real failures across 412 rendered route-states:
 *
 *   a semantic colour is tuned as a FILL and then used as TEXT on a tint of
 *   ITSELF. --success at its brand lightness is 3.15:1 on white and 2.72:1 on its
 *   own badge wash. Every one of those failures was invisible to a check that only
 *   looked at fg-on-bg.
 *
 * So the rule here is: check each colour against the ground it ACTUALLY lands on,
 * and when it fails, solve for the value that passes and hand that back. An author
 * choosing a brand colour cannot be expected to know that a green which looks fine
 * as a button is unreadable as a status label.
 *
 * Pure functions over hex — no DOM, no rendering. That is a deliberate limit:
 * this catches palette-level mistakes at authoring time. It cannot see gradients,
 * blur or stacked translucency, which is why the pixel-measuring sweep
 * (.audit/drive.mjs) still exists and still finds different things.
 */

export type ContrastIssue = {
  level: "error" | "warning";
  /** Which pairing failed, in an author's language. */
  what: string;
  ratio: number;
  required: number;
  /** The field to change, so a UI can focus it. */
  field: keyof ThemeConfig;
  /** A value that passes, same hue. Null when no lightness of that hue works. */
  suggestion: string | null;
  message: string;
};

// ── colour maths ─────────────────────────────────────────────────────────────

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

export function parseHex(c: string): [number, number, number] | null {
  if (typeof c !== "string") return null;
  const s = c.trim();
  if (!HEX.test(s)) return null;
  const h = s.length === 4
    ? `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`
    : s;
  return [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16)) as [number, number, number];
}

const lin = (v: number) => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
const lum = ([r, g, b]: [number, number, number]) => 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);

export function ratio(a: [number, number, number], b: [number, number, number]): number {
  const [x, y] = [lum(a), lum(b)];
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}

const mix = (a: [number, number, number], b: [number, number, number], t: number) =>
  a.map((v, i) => v + (b[i] - v) * t) as [number, number, number];

const toHex = (c: [number, number, number]) =>
  "#" + c.map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, "0")).join("");

/**
 * The wash a tinted badge sits on: 14% of the colour over the surface. Matches
 * `--brand-muted` in build-skin-css (mix(primary, 14)) so the check measures the
 * ground the product actually paints, not a hypothetical one.
 */
export const WASH_ALPHA = 0.14;
export const washOver = (colour: string, surface: string): [number, number, number] | null => {
  const c = parseHex(colour), s = parseHex(surface);
  return c && s ? mix(s, c, WASH_ALPHA) : null;
};

/**
 * Nudge `colour` toward `toward` until it clears `need` against `ground`.
 * Keeps the hue: this darkens or lightens, it does not pick a different colour.
 */
function solve(
  colour: string,
  ground: [number, number, number],
  need: number,
  toward: [number, number, number],
  /** When the ground is a tint of the colour itself, it MOVES as the colour moves. */
  ownWashOver?: [number, number, number],
): string | null {
  const c = parseHex(colour);
  if (!c) return null;
  for (let t = 0; t <= 1.0001; t += 0.02) {
    const cand = mix(c, toward, t);
    // Recompute the badge tint from the CANDIDATE, not from the original colour.
    // Solving against the old wash produced suggestions that did not actually fix
    // the issue they were offered for — caught by asserting that every suggestion
    // passes its own check, which is the only way this class of bug shows up.
    const g = ownWashOver ? mix(ownWashOver, cand, WASH_ALPHA) : ground;
    if (ratio(cand, g) >= need) return toHex(cand);
  }
  return null;
}

/** Darker or lighter, whichever direction the ground calls for. */
const towardReadable = (ground: [number, number, number]): [number, number, number] =>
  (lum(ground) > 0.5 ? [0, 0, 0] : [255, 255, 255]);

/**
 * Try BOTH directions and take whichever reaches the target with less movement.
 *
 * Guessing one direction from the ground's lightness is wrong whenever the INK is
 * already at an extreme: white text on a mid-orange button was reported as "no
 * fix" because the ground was darkish, so the solver pushed white toward white.
 * The answer was to darken the ink, which it never tried.
 */
function solveEitherWay(
  colour: string, ground: [number, number, number], need: number,
  ownWashOver?: [number, number, number],
): string | null {
  const dark = solve(colour, ground, need, [0, 0, 0], ownWashOver);
  const light = solve(colour, ground, need, [255, 255, 255], ownWashOver);
  if (!dark) return light;
  if (!light) return dark;
  const c = parseHex(colour)!;
  const dist = (h: string) => { const x = parseHex(h)!; return Math.abs(lum(x) - lum(c)); };
  return dist(dark) <= dist(light) ? dark : light;
}

/**
 * The colour to use as TEXT for a semantic hue, given the surface it sits on.
 *
 * A semantic colour has two jobs and one value: it fills a badge and it labels
 * one. Those need different lightnesses, which is why every shipped skin measures
 * 2.1-3.3:1 on its own badge. Deriving the ink means a brand picks ONE colour and
 * the readable variant follows, instead of being told its green is wrong.
 */
export function readableInk(colour: string, surface: string, need = 4.5): string {
  const s = parseHex(surface);
  const wash = washOver(colour, surface);
  if (!s || !wash) return colour;
  const c = parseHex(colour);
  if (c && ratio(c, wash) >= need) return colour;
  return solveEitherWay(colour, wash, need, s) ?? colour;
}

// ── the checks ───────────────────────────────────────────────────────────────

type Pairing = {
  what: string;
  field: keyof ThemeConfig;
  ink: (c: ThemeConfig) => string | undefined;
  ground: (c: ThemeConfig) => string | undefined;
  /** Ground is a 14% tint of the ink itself — the badge case. */
  onOwnWash?: boolean;
  /** The colour the wash is made FROM, when the ink is a derived variant of it. */
  washFrom?: (c: ThemeConfig) => string | undefined;
  need: number;
  level: "error" | "warning";
};

const PAIRINGS: Pairing[] = [
  { what: "Body text on the page background", field: "fg", ink: (c) => c.fg, ground: (c) => c.bg, need: 4.5, level: "error" },
  { what: "Body text on a card", field: "fg", ink: (c) => c.fg, ground: (c) => c.surface, need: 4.5, level: "error" },
  // A button LABEL is body text. The old check allowed 3:1 here, which is the
  // large-text threshold and lets a 14px label through at nearly half the contrast
  // it needs.
  { what: "Label on a primary button", field: "primaryFg", ink: (c) => c.primaryFg, ground: (c) => c.primary, need: 4.5, level: "error" },
  { what: "Secondary text on a card", field: "muted", ink: (c) => c.muted, ground: (c) => c.surface, need: 4.5, level: "error" },
  { what: "Secondary text on the page background", field: "muted", ink: (c) => c.muted, ground: (c) => c.bg, need: 4.5, level: "warning" },
  // The ones the old validator could not see. A semantic hue is authored as a fill
  // and then used as the LABEL of a badge tinted with itself.
  { what: "Success text on a success badge", field: "success", ink: (c) => readableInk(c.success ?? "#2fa36b", c.surface), ground: (c) => c.surface, onOwnWash: true, need: 4.5, level: "warning", washFrom: (c) => c.success ?? "#2fa36b" },
  { what: "Warning text on a warning badge", field: "warning", ink: (c) => readableInk(c.warning ?? "#d69e2e", c.surface), ground: (c) => c.surface, onOwnWash: true, need: 4.5, level: "warning", washFrom: (c) => c.warning ?? "#d69e2e" },
  { what: "Danger text on a danger badge", field: "danger", ink: (c) => readableInk(c.danger ?? "#e5484d", c.surface), ground: (c) => c.surface, onOwnWash: true, need: 4.5, level: "warning", washFrom: (c) => c.danger ?? "#e5484d" },
  { what: "Brand text on a brand-tinted chip", field: "primary", ink: (c) => readableInk(c.primary, c.surface), ground: (c) => c.surface, onOwnWash: true, need: 4.5, level: "warning", washFrom: (c) => c.primary },
  { what: "Gold text on a gold-tinted badge", field: "gold", ink: (c) => readableInk(c.gold ?? "#c9a24b", c.surface), ground: (c) => c.surface, onOwnWash: true, need: 4.5, level: "warning", washFrom: (c) => c.gold ?? "#c9a24b" },
];

/**
 * Check a brand's palette. Returns only failures, worst first.
 *
 * Pairings whose colours are not plain hex are SKIPPED rather than guessed at —
 * `color-mix()` and `oklch()` are legal here and resolving them properly needs a
 * browser. Silently passing something unmeasured would be worse than saying
 * nothing, so the sweep remains the backstop for those.
 */
export function checkBrandContrast(c: ThemeConfig): ContrastIssue[] {
  const out: ContrastIssue[] = [];
  for (const p of PAIRINGS) {
    const inkHex = p.ink(c);
    const groundHex = p.ground(c);
    if (!inkHex || !groundHex) continue;
    const ink = parseHex(inkHex);
    if (!ink) continue;
    // The wash is tinted with the brand's chosen colour; the ink may be a derived
    // variant of it. Measuring the derived ink against a wash made from the
    // DERIVED value would flatter the result — the badge is tinted with what the
    // author picked.
    const washSource = p.washFrom?.(c) ?? inkHex;
    const ground = p.onOwnWash ? washOver(washSource, groundHex) : parseHex(groundHex);
    if (!ground) continue;

    const r = ratio(ink, ground);
    if (r >= p.need) continue;

    const surfaceRgb = p.onOwnWash ? parseHex(groundHex) : null;
    const suggestion = solveEitherWay(inkHex, ground, p.need, surfaceRgb ?? undefined);
    out.push({
      level: p.level,
      what: p.what,
      ratio: Math.round(r * 100) / 100,
      required: p.need,
      field: p.field,
      suggestion,
      message:
        `${p.what} is ${r.toFixed(2)}:1, below the ${p.need}:1 needed to read.` +
        (suggestion
          ? ` Use ${suggestion} for ${String(p.field)} (${ratio(
              parseHex(suggestion)!,
              surfaceRgb ? mix(surfaceRgb, parseHex(suggestion)!, WASH_ALPHA) : ground,
            ).toFixed(2)}:1), or keep this colour as a fill only.`
          : ` No lightness of this hue reaches ${p.need}:1 here — pick a different colour for ${String(p.field)}.`),
    });
  }
  return out.sort((a, b) => a.ratio - b.ratio);
}
